import { Vector2 } from "../../../common/vector2.js";
import { Game } from "../../../game.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ImagesHolder } from "../../../image_loader/images_holder.js";
import type { OfferRewardDto } from "../../../api/dto/offers.dto.js";
import type { GameStatistics } from "../../../storage/statistics_storage.js";
import { mysteryBoxAmountText, mysteryBoxIconSize, mysteryBoxRewardIcon } from "../../../offers/mystery_box_offer.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformRotate } from "../../../transforms/transform_rotate.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { RewardFrame } from "../../common_ui_elements/reward_frame.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { UICanvasRect } from "../../common_ui_elements/ui_canvas_rect.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { RewardFlySource, RewardFlyToHud } from "../../reward_fly/reward_fly_to_hud.js";
import { RewardTooltip } from "../../tooltip/reward_tooltip.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";

export interface MysteryBoxOverlayParams
{
  /** What the server granted; already applied to the economy by the caller. */
  rewards: OfferRewardDto[];
  /** Balances before the grant, so the HUD can count up to the new ones. */
  stats_before: GameStatistics;
  on_closed?: () => void;
}

/**
 * Result of the Mystery Box offer (Figma "Flow 2"): the chest bounces open,
 * the granted rewards pop out under it, and "Claim" flies them to the HUD.
 * Laid out on the 1080x1920 frame and centered vertically on taller screens.
 */
export class MysteryBoxOverlay extends AbstractOverlay
{
  // Chest parts, design px. The bottom's lower edge is the "ground" the lid swaps on.
  private static readonly CHEST_GROUND_Y = 856;
  private static readonly MAIN_W = 354;
  private static readonly MAIN_H = 199;
  private static readonly CLOSED_W = 399;
  private static readonly CLOSED_H = 284;
  private static readonly CLOSED_OFFSET_Y = 158;
  private static readonly OPENED_W = 362;
  private static readonly OPENED_H = 192;
  private static readonly OPENED_OFFSET_Y = 172;

  private static readonly REWARD_Y = 972;
  private static readonly REWARD_PITCH = 180;
  private static readonly REWARDS_PER_ROW = 4;
  private static readonly REWARD_ROW_PITCH = 222;

  /** Same coin flight as the Adventure and daily-task chest popups (COIN_FLY_SPEED). */
  private static readonly COIN_FLY_SPEED = 1.5;

  private static readonly GLOW_SIZE = 760;
  private static readonly GLOW_ALPHA = 0.55;
  private static readonly GLOW_ROTATION_MS = 14000;
  private static readonly GLOW_TINT: [ number, number, number ] = [ 1.0, 0.82, 0.35 ];

  private readonly design_x: number;
  private readonly design_y: number;
  private readonly overlay_params: MysteryBoxOverlayParams;

  private backdrop!: UICanvasRect;
  private title!: UIImage;
  private chest_main!: UIImage;
  private chest_top!: UIImage;
  private glow!: UIElementHolder;
  private frames: RewardFrame[] = [];
  private claim_button!: UINineSliceElement;
  private claimed = false;
  private close_notified = false;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, params: MysteryBoxOverlayParams, overlay_id: OverlayId )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );
    this.overlay_params = params;
    this.design_x = ( this.rect.getW() - LayoutScaler.widthFromDesign( 1080 ) ) / 2;
    this.design_y = ( this.rect.getH() - LayoutScaler.heightFromDesign( 1920 ) ) / 2;

    this.createBackdrop();
    this.createGlow();
    this.createTitle();
    this.createChest();
    this.createRewards();
    this.createClaimButton();
    this.playAppear();
  }

  public name(): string { return "MysteryBoxOverlay"; }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public override onOverlayWillClose(): void
  {
    if ( this.close_notified ) return;
    this.close_notified = true;
    this.overlay_params.on_closed?.();
  }

  private x( design_x: number ): number
  {
    return this.design_x + LayoutScaler.xFromDesign( design_x );
  }

  private y( design_y: number ): number
  {
    return this.design_y + LayoutScaler.yFromDesign( design_y );
  }

  /**
   * The dim is drawn here (Figma: black at 80%) instead of by the CSS
   * #overlayBackground, so it can fade out the moment "Claim" is tapped;
   * the CSS one would stay until the overlay closes and, stacked on this
   * one, turned the lobby almost black.
   */
  public override getViewportBackground(): string | null
  {
    return "transparent";
  }

  private createBackdrop(): void
  {
    // Spans the physical canvas: the overlay rect stops at the safe areas.
    this.backdrop = new UICanvasRect(
      this.ctx,
      -Game.safeAreaLeft(),
      -Game.safeAreaTop(),
      this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight(),
      this.rect.getH() + Game.safeAreaTop() + Game.safeAreaBottom(),
      "rgba(0, 0, 0, 0.8)",
    );
    this.backdrop.setOpacityImmediate( 0 );
    this.backdrop.setOnClick( () => {} );
    this.addChild( this.backdrop );
  }

  private createTitle(): void
  {
    this.title = new UIImage(
      this.ctx,
      this.x( 104 ),
      this.y( 140 ),
      LayoutScaler.widthFromDesign( 870 ),
      LayoutScaler.heightFromDesign( 321 ),
      ImgID.UI_MysteryBoxTitle,
    );
    this.title.setOpacityImmediate( 0 );
    this.addChild( this.title );
  }

  private chestGroundY(): number
  {
    return this.y( MysteryBoxOverlay.CHEST_GROUND_Y );
  }

  private createChest(): void
  {
    const center_x = this.x( 540 );
    const main_w = LayoutScaler.widthFromDesign( MysteryBoxOverlay.MAIN_W );
    const main_h = LayoutScaler.heightFromDesign( MysteryBoxOverlay.MAIN_H );
    const main_y = this.chestGroundY() - main_h;
    this.chest_main = new UIImage( this.ctx, center_x - main_w / 2, main_y, main_w, main_h, ImgID.UI_MysteryBoxChestBottom );

    const top_w = LayoutScaler.widthFromDesign( MysteryBoxOverlay.CLOSED_W );
    const top_h = LayoutScaler.heightFromDesign( MysteryBoxOverlay.CLOSED_H );
    this.chest_top = new UIImage(
      this.ctx,
      center_x - top_w / 2,
      main_y - LayoutScaler.yFromDesign( MysteryBoxOverlay.CLOSED_OFFSET_Y ),
      top_w,
      top_h,
      ImgID.UI_MysteryBoxChestClosedTop,
    );

    for ( const part of [ this.chest_main, this.chest_top ] )
    {
      part.setOpacityImmediate( 0 );
      this.addChild( part );
    }
  }

  private swapLidToOpen(): void
  {
    const main_y = this.chestGroundY() - LayoutScaler.heightFromDesign( MysteryBoxOverlay.MAIN_H );
    const w = LayoutScaler.widthFromDesign( MysteryBoxOverlay.OPENED_W );
    const h = LayoutScaler.heightFromDesign( MysteryBoxOverlay.OPENED_H );
    this.chest_top.setImageId( ImgID.UI_MysteryBoxChestOpenedTop );
    this.chest_top.setBounds( this.x( 540 ) - w / 2, main_y - LayoutScaler.yFromDesign( MysteryBoxOverlay.OPENED_OFFSET_Y ), w, h );
  }

  private createGlow(): void
  {
    const size = LayoutScaler.widthFromDesign( MysteryBoxOverlay.GLOW_SIZE );
    const center_y = this.y( MysteryBoxOverlay.CHEST_GROUND_Y - 150 );
    this.glow = new UIElementHolder( this.ctx, this.x( 540 ) - size / 2, center_y - size / 2, size, size, false );
    this.glow.setOpacityImmediate( 0 );

    const now = performance.now();
    const layers: UIImage[] = [];
    for ( const direction of [ 1, -1 ] )
    {
      const layer = new UIImage( this.ctx, 0, 0, size, size, ImgID.RaysGlow1 );
      layers.push( layer );
      layer.enableRotation();
      layer.addTransform( new TransformRotate(
        layer, true, 0, direction * Math.PI * 2, now, MysteryBoxOverlay.GLOW_ROTATION_MS,
        { easing: Easing.Linear, infinite_loop: true, decorative: true },
      ) );
      this.glow.addChild( layer );
    }
    this.addChild( this.glow );

    void MysteryBoxOverlay.tintedGlow().then( bitmap =>
    {
      if ( bitmap ) for ( const layer of layers ) layer.setImageBitmap( bitmap );
    } );
  }

  private static tinted_glow: Promise<ImageBitmap | null> | null = null;

  /** RaysGlow1 recolored gold once, like the other chest popups do. */
  private static tintedGlow(): Promise<ImageBitmap | null>
  {
    this.tinted_glow ??= this.createTintedGlow().catch( () => null );
    return this.tinted_glow;
  }

  private static async createTintedGlow(): Promise<ImageBitmap | null>
  {
    const source = ImagesHolder.getImage( ImgID.RaysGlow1 );
    if ( !source || !source.width ) return null;

    const canvas = document.createElement( "canvas" );
    canvas.width = source.width;
    canvas.height = source.height;
    // CPU-backed, so the readback below does not stall on the GPU.
    const ctx = canvas.getContext( "2d", { willReadFrequently: true } );
    if ( !ctx ) return null;
    ctx.drawImage( source, 0, 0 );
    const image = ctx.getImageData( 0, 0, canvas.width, canvas.height );
    const [ r, g, b ] = MysteryBoxOverlay.GLOW_TINT;
    for ( let i = 0; i < image.data.length; i += 4 )
    {
      image.data[i] = Math.min( 255, image.data[i] * r );
      image.data[i + 1] = Math.min( 255, image.data[i + 1] * g );
      image.data[i + 2] = Math.min( 255, image.data[i + 2] * b );
    }
    ctx.putImageData( image, 0, 0 );
    return createImageBitmap( canvas );
  }

  private createRewards(): void
  {
    const rewards = this.overlay_params.rewards;
    const per_row = MysteryBoxOverlay.REWARDS_PER_ROW;
    const icon_slot = LayoutScaler.widthFromDesign( 150 );

    rewards.forEach( ( reward, index ) =>
    {
      const row = Math.floor( index / per_row );
      const in_row = Math.min( per_row, rewards.length - row * per_row );
      const col = index % per_row;
      const row_w = ( in_row - 1 ) * MysteryBoxOverlay.REWARD_PITCH + RewardFrame.COMPACT_DESIGN_W;
      const design_x = 540 - row_w / 2 + col * MysteryBoxOverlay.REWARD_PITCH;
      const icon = mysteryBoxIconSize( reward.type, icon_slot );

      const frame = new RewardFrame( this.ctx, this.x( design_x ), this.y( MysteryBoxOverlay.REWARD_Y + row * MysteryBoxOverlay.REWARD_ROW_PITCH ), {
        icon_id: mysteryBoxRewardIcon( reward.type ),
        amount_text: mysteryBoxAmountText( reward ),
        variant: "compact",
        icon_width: icon.w,
        icon_height: icon.h,
      } );
      frame.setOpacityImmediate( 0 );
      frame.setScaleImmediate( new Vector2( 0, 0 ) );
      RewardTooltip.attach( frame, reward.type );
      this.addChild( frame );
      this.frames.push( frame );
    } );
  }

  private createClaimButton(): void
  {
    const rows = Math.max( 1, Math.ceil( this.overlay_params.rewards.length / MysteryBoxOverlay.REWARDS_PER_ROW ) );
    const w = LayoutScaler.widthFromDesign( 570 );
    const h = LayoutScaler.heightFromDesign( 250 );
    const button = new UINineSliceElement(
      this.ctx,
      this.x( 255 ),
      this.y( 1420 + ( rows - 1 ) * MysteryBoxOverlay.REWARD_ROW_PITCH ),
      w,
      h,
      ImgID.UI_GreenFrameBtn,
      "frame_btn",
      undefined,
      undefined,
      true,
    );
    button.addChild( new UITextElement( this.ctx, 0, LayoutScaler.yFromDesign( 4 ), w, h, {
      localization_key: "CLAIM",
      font_size: LayoutScaler.textSizeFromDesign( 72 ),
      font_weight: 900,
      text_style: TextStyleScheme.geologica,
      text_color: "#FFFFFF",
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
      scale_to_width: true,
      min_font_size: LayoutScaler.textSizeFromDesign( 44 ),
      ...TextStyleScheme.buttonShadow(),
      outline_color: ColorScheme.more_lives_buy_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
    } ), true );
    button.setOpacityImmediate( 0 );
    button.setClickable( false );
    button.setOnClick( () => this.claim() );
    this.addChild( button );
    this.claim_button = button;
  }

  // ---- animation ----

  private playAppear(): void
  {
    const APPEAR_MS = 160;
    const now = performance.now();
    this.backdrop.fadeIn( APPEAR_MS, now, Easing.EaseOutQuad );
    this.title.fadeIn( APPEAR_MS, now, Easing.EaseOutQuad );
    for ( const part of [ this.chest_main, this.chest_top ] )
    {
      part.fadeIn( APPEAR_MS, now, Easing.EaseOutQuad );
      part.addTransform( new TransformScale( part, true, new Vector2( 0.6, 0.6 ), new Vector2( 1, 1 ), now, APPEAR_MS + 60, { easing: Easing.EaseOutQuad } ) );
    }
    RewardFlyToHud.schedule( this.ctx, this, now, now + APPEAR_MS + 260, () => this.playOpen() );
  }

  /** Same squash → stretch → settle beat as the Adventure/daily chests, with the lid swapped at the top of the stretch. */
  private playOpen(): void
  {
    const SQUASH_MS = 110;
    const UNSQUASH_MS = 100;
    const GROW_MS = 80;
    const SHRINK_MS = 110;

    const normal = new Vector2( 1, 1 );
    const squashed = new Vector2( 1.05, 0.6 );
    const grown = new Vector2( 0.95, 1.2 );
    const ground = this.chestGroundY();

    const both = ( from: Vector2, to: Vector2, ms: number, easing: Easing, done?: () => void ): void =>
    {
      const start = performance.now();
      this.scaleFromBottom( this.chest_main, ground, from, to, start, ms, easing, done );
      this.scaleFromBottom( this.chest_top, ground, from, to, start, ms, easing );
    };

    both( normal, squashed, SQUASH_MS, Easing.EaseInQuad, () =>
      both( squashed, normal, UNSQUASH_MS, Easing.EaseOutQuad, () =>
      {
        this.swapLidToOpen();
        this.glow.fadeToOpacity( MysteryBoxOverlay.GLOW_ALPHA, 220, performance.now(), Easing.EaseOutQuad );
        both( normal, grown, GROW_MS, Easing.EaseOutQuad, () =>
          both( grown, normal, SHRINK_MS, Easing.EaseInQuad, () => this.revealRewards() ) );
      } ) );
  }

  private scaleFromBottom( target: UIElement, ground_y: number, from: Vector2, to: Vector2, start: number, ms: number, easing: Easing, done?: () => void ): void
  {
    const d = target.pixelPosImportant().y + target.getRect().getH() / 2 - ground_y;
    const scale = new TransformScale( target, true, from, to, start, ms, { easing } );
    if ( done ) scale.setOnFinished( done );
    target.addTransform( scale );
    target.addTransform( new TransformStraightMove( target, false, new Vector2( 0, d * ( from.y - 1 ) ), new Vector2( 0, d * ( to.y - 1 ) ), start, ms, { easing } ) );
  }

  private revealRewards(): void
  {
    const POP_UP_MS = 150;
    const POP_SETTLE_MS = 100;
    const STAGGER_MS = 90;
    const now = performance.now();

    this.frames.forEach( ( frame, index ) =>
    {
      const start = now + index * STAGGER_MS;
      frame.fadeIn( POP_UP_MS, start, Easing.EaseOutQuad );
      frame.addTransform( new TransformScale( frame, true, new Vector2( 0, 0 ), new Vector2( 1.15, 1.15 ), start, POP_UP_MS, { easing: Easing.EaseOutQuad } ) );
      frame.addTransform( new TransformScale( frame, true, new Vector2( 1.15, 1.15 ), new Vector2( 1, 1 ), start + POP_UP_MS, POP_SETTLE_MS, { easing: Easing.EaseInQuad } ) );
    } );

    const button_at = now + Math.max( 0, this.frames.length - 1 ) * STAGGER_MS + POP_UP_MS;
    this.claim_button.fadeIn( 180, button_at, Easing.EaseOutQuad );
    RewardFlyToHud.schedule( this.ctx, this, now, button_at, () => this.claim_button.setClickable( true ) );
  }

  private claim(): void
  {
    if ( this.claimed ) return;
    this.claimed = true;
    this.claim_button.setClickable( false );

    const sources = this.frames.map( ( frame, index ): RewardFlySource =>
    {
      const reward = this.overlay_params.rewards[index];
      const is_heart = reward.type === "hearts" || reward.type === "infinite_hearts_minutes";
      const pos = frame.icon.getAbsolutePosition();
      const rect = frame.icon.getRect();
      return {
        target: reward.type === "coins" ? "coin" : is_heart ? "heart" : "avatar",
        img_id: mysteryBoxRewardIcon( reward.type ),
        center: new Vector2( pos.x + rect.getW() / 2, pos.y + rect.getH() / 2 ),
        size: rect.getW(),
        amount: reward.amount,
        copies: reward.type === "hearts" ? Math.max( 1, Math.min( 3, reward.amount ) ) : 1,
      };
    } );

    const now = performance.now();
    const FADE_MS = 160;
    for ( const element of [ ...this.frames, this.claim_button, this.title, this.chest_main, this.chest_top, this.glow ] )
      element.fadeOut( FADE_MS, now, Easing.EaseInQuad );

    // The dim goes with the chest, like StarChestOpenPopup's: the rewards fly
    // over the lobby itself, onto a HUD copy drawn right over the real one.
    this.backdrop.fadeOut( FADE_MS, now, Easing.EaseInQuad );
    const finish = RewardFlyToHud.play( this.ctx, this, sources, this.overlay_params.stats_before, MysteryBoxOverlay.COIN_FLY_SPEED );
    RewardFlyToHud.schedule( this.ctx, this, now, Math.max( finish, now + FADE_MS ), () =>
    {
      if ( OverlayManager.isOverlayOpen( this.getId() ) )
        OverlayManager.closeOverlay();
    } );
  }
}
